'use client';

import React from 'react';
import { DrawingObject, Wall, Door, Window } from '@/types';
import { getWallConstruction, WALL_CONSTRUCTIONS } from '@/lib/wall-constructions';

interface PropertiesPanelProps {
  selectedObject: DrawingObject | null;
  onUpdate: (obj: DrawingObject) => void;
  onDelete: (id: string) => void;
}

export default function PropertiesPanel({
  selectedObject,
  onUpdate,
  onDelete,
}: PropertiesPanelProps) {
  if (!selectedObject) {
    return (
      <div className="w-64 bg-gray-900 text-gray-400 flex flex-col items-center justify-center border-l border-gray-800">
        <div className="text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-gray-800/60 border border-gray-700/50 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl opacity-50">📐</span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Markera ett objekt för att se dess egenskaper
          </p>
          <div className="mt-4 flex items-center gap-2 justify-center text-[10px] text-gray-600">
            <kbd className="bg-gray-800 px-1.5 py-0.5 rounded border border-gray-700 font-mono">V</kbd>
            <span>välj verktyg</span>
          </div>
        </div>
      </div>
    );
  }

  if (selectedObject.type === 'wall') {
    return <WallProperties wall={selectedObject} onUpdate={onUpdate} onDelete={onDelete} />;
  }

  if (selectedObject.type === 'door') {
    return <DoorProperties door={selectedObject} onUpdate={onUpdate} onDelete={onDelete} />;
  }

  if (selectedObject.type === 'window') {
    return <WindowProperties windowObj={selectedObject} onUpdate={onUpdate} onDelete={onDelete} />;
  }

  return null;
}

// ============================================
// Shared UI helpers
// ============================================

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest mb-2 px-0.5">
      {children}
    </h3>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <label className="text-[10px] text-gray-500 block mb-1 uppercase tracking-wider">{children}</label>;
}

function ReadOnlyValue({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-gray-800/80 rounded-lg px-3 py-2 text-sm font-mono text-gray-200 border border-gray-700/50">
      {children}
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />;
}

// ============================================
// Wall Properties
// ============================================

function WallProperties({
  wall,
  onUpdate,
  onDelete,
}: {
  wall: Wall;
  onUpdate: (obj: DrawingObject) => void;
  onDelete: (id: string) => void;
}) {
  const construction = getWallConstruction(wall.construction);
  const dx = wall.x2 - wall.x1;
  const dy = wall.y2 - wall.y1;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
  const isExterior = wall.construction.startsWith('ext');

  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col border-l border-gray-800">
      <div className="p-3 border-b border-gray-800">
        <SectionLabel>Vägg</SectionLabel>
        <p className="text-sm text-gray-200 font-medium">
          {construction?.name || 'Okänd konstruktion'}
        </p>
      </div>

      <div className="p-3 space-y-3">
        {/* Length */}
        <div>
          <FieldLabel>Längd</FieldLabel>
          <ReadOnlyValue>
            {(length * 1000).toFixed(0)} mm <span className="text-gray-500 text-xs">({length.toFixed(2)} m)</span>
          </ReadOnlyValue>
        </div>

        {/* Thickness */}
        <div>
          <FieldLabel>Tjocklek</FieldLabel>
          <ReadOnlyValue>{construction?.thickness || '?'} mm</ReadOnlyValue>
        </div>

        {/* Angle */}
        <div>
          <FieldLabel>Vinkel</FieldLabel>
          <ReadOnlyValue>{angle.toFixed(1)}°</ReadOnlyValue>
        </div>

        {/* Construction layers */}
        {construction && (
          <div>
            <FieldLabel>Lager</FieldLabel>
            <div className="bg-gray-800/80 rounded-lg p-2.5 text-[11px] space-y-1 border border-gray-700/50">
              {construction.layers.map((layer, i) => (
                <div key={i} className="text-gray-300 flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-gray-600 shrink-0" />
                  {layer}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* In-/utsida (ytterväggar) */}
        {isExterior && (
          <div>
            <FieldLabel>In-/utsida</FieldLabel>
            <button
              onClick={() => onUpdate({ ...wall, flipped: !wall.flipped })}
              className="w-full px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-blue-500/40 text-gray-200 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
              title="Byt vilken sida som räknas som utsida (påverkar Y/I-mått och face-snap)"
            >
              ⇄ Vänd vägg {wall.flipped ? '(vänd från standard)' : '(standard)'}
            </button>
            <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
              Standard gissas automatiskt utifrån hur väggen ritades. Stämmer in-/utsidan inte
              (t.ex. vid ett konkavt hörn), vänd den här.
            </p>
          </div>
        )}

        <Divider />

        {/* Construction type selector */}
        <div>
          <FieldLabel>Ändra konstruktion</FieldLabel>
          <select
            value={wall.construction}
            onChange={(e) => onUpdate({ ...wall, construction: e.target.value })}
            className="w-full bg-gray-800 rounded-lg px-3 py-2 text-sm border border-gray-700 focus:border-blue-500/50 focus:outline-none transition-colors"
          >
            {WALL_CONSTRUCTIONS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.thickness}mm)
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Delete button */}
      <div className="p-3 mt-auto border-t border-gray-800">
        <button
          onClick={() => onDelete(wall.id)}
          className="w-full px-3 py-2 bg-red-600/10 hover:bg-red-600/20 border border-red-500/20 hover:border-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-all"
        >
          🗑 Ta bort vägg
        </button>
      </div>
    </div>
  );
}

// ============================================
// Door Properties
// ============================================

function DoorProperties({
  door,
  onUpdate,
  onDelete,
}: {
  door: Door;
  onUpdate: (obj: DrawingObject) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col border-l border-gray-800">
      <div className="p-3 border-b border-gray-800">
        <SectionLabel>Dörr</SectionLabel>
      </div>

      <div className="p-3 space-y-3">
        {/* Width */}
        <div>
          <FieldLabel>Bredd</FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0.6}
              max={2.0}
              step={0.1}
              value={door.width}
              onChange={(e) => onUpdate({ ...door, width: parseFloat(e.target.value) })}
              className="flex-1 accent-blue-500"
            />
            <span className="text-sm font-mono w-14 text-right text-gray-200">
              {(door.width * 100).toFixed(0)} cm
            </span>
          </div>
        </div>

        {/* Height */}
        <div>
          <FieldLabel>Höjd</FieldLabel>
          <ReadOnlyValue>{(door.height * 100).toFixed(0)} cm</ReadOnlyValue>
        </div>

        <Divider />

        {/* Standard sizes */}
        <div>
          <FieldLabel>Standardmått</FieldLabel>
          <div className="grid grid-cols-2 gap-1">
            {[0.6, 0.7, 0.8, 0.9, 1.0, 1.2].map((w) => (
              <button
                key={w}
                onClick={() => onUpdate({ ...door, width: w })}
                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                  Math.round(door.width * 100) === Math.round(w * 100)
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-700/50'
                }`}
              >
                {(w * 100).toFixed(0)} cm
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-3 mt-auto border-t border-gray-800">
        <button
          onClick={() => onDelete(door.id)}
          className="w-full px-3 py-2 bg-red-600/10 hover:bg-red-600/20 border border-red-500/20 hover:border-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-all"
        >
          🗑 Ta bort dörr
        </button>
      </div>
    </div>
  );
}

// ============================================
// Window Properties
// ============================================

function WindowProperties({
  windowObj: win,
  onUpdate,
  onDelete,
}: {
  windowObj: Window;
  onUpdate: (obj: DrawingObject) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="w-64 bg-gray-900 text-white flex flex-col border-l border-gray-800">
      <div className="p-3 border-b border-gray-800">
        <SectionLabel>Fönster</SectionLabel>
      </div>

      <div className="p-3 space-y-3">
        {/* Width */}
        <div>
          <FieldLabel>Bredd</FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0.4}
              max={3.0}
              step={0.1}
              value={win.width}
              onChange={(e) => onUpdate({ ...win, width: parseFloat(e.target.value) })}
              className="flex-1 accent-blue-500"
            />
            <span className="text-sm font-mono w-14 text-right text-gray-200">
              {(win.width * 100).toFixed(0)} cm
            </span>
          </div>
        </div>

        {/* Height */}
        <div>
          <FieldLabel>Höjd</FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0.4}
              max={2.4}
              step={0.1}
              value={win.height}
              onChange={(e) => onUpdate({ ...win, height: parseFloat(e.target.value) })}
              className="flex-1 accent-blue-500"
            />
            <span className="text-sm font-mono w-14 text-right text-gray-200">
              {(win.height * 100).toFixed(0)} cm
            </span>
          </div>
        </div>

        {/* Sill height */}
        <div>
          <FieldLabel>Karmhöjd (från golv)</FieldLabel>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0.3}
              max={1.5}
              step={0.1}
              value={win.sillHeight}
              onChange={(e) => onUpdate({ ...win, sillHeight: parseFloat(e.target.value) })}
              className="flex-1 accent-blue-500"
            />
            <span className="text-sm font-mono w-14 text-right text-gray-200">
              {(win.sillHeight * 100).toFixed(0)} cm
            </span>
          </div>
        </div>

        <Divider />

        {/* Standard sizes */}
        <div>
          <FieldLabel>Standardmått</FieldLabel>
          <div className="grid grid-cols-2 gap-1">
            {[
              { w: 0.6, h: 0.6, label: '60×60' },
              { w: 0.6, h: 1.2, label: '60×120' },
              { w: 0.9, h: 1.2, label: '90×120' },
              { w: 1.2, h: 1.4, label: '120×140' },
              { w: 1.5, h: 1.4, label: '150×140' },
              { w: 1.8, h: 1.4, label: '180×140' },
            ].map((s) => (
              <button
                key={s.label}
                onClick={() => onUpdate({ ...win, width: s.w, height: s.h })}
                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                  Math.round(win.width * 100) === Math.round(s.w * 100) && Math.round(win.height * 100) === Math.round(s.h * 100)
                    ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/20'
                    : 'bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-200 border border-gray-700/50'
                }`}
              >
                {s.label} cm
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-3 mt-auto border-t border-gray-800">
        <button
          onClick={() => onDelete(win.id)}
          className="w-full px-3 py-2 bg-red-600/10 hover:bg-red-600/20 border border-red-500/20 hover:border-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-all"
        >
          🗑 Ta bort fönster
        </button>
      </div>
    </div>
  );
}
